class HeaderMegaMenu {
  constructor(header) {
    this.header = header;
    this.sectionRoot = header.closest('[id^="shopify-section-"]') || header.parentElement;
    this.announcementBar = header.querySelector('[data-announcement-bar]');
    this.announcementSurface = header.querySelector('[data-announcement-surface]');
    this.announcementClose = header.querySelector('.announcement-close-btn');
    this.searchToggle = header.querySelector('[data-search-toggle]');
    this.searchClose = header.querySelector('[data-search-close]');
    this.searchOverlay = header.querySelector('[data-search-overlay]');
    this.searchPanel = header.querySelector('[data-search-panel]');
    this.searchForm = header.querySelector('[data-autocomplete-true]');
    this.searchInput = header.querySelector('[data-search-input]');
    this.predictiveSearch = header.querySelector('[data-predictive-search]');
    this.predictiveSearchResults = header.querySelector('[data-predictive-search-results]');
    this.megaRoot = header.querySelector('[data-mega-menu-root]');
    this.nav = header.querySelector('.header-mega__nav');
    this.navTriggers = this.megaRoot ? Array.from(header.querySelectorAll('[data-nav-trigger]')) : [];
    this.closeTriggers = this.megaRoot ? Array.from(header.querySelectorAll('[data-nav-close]')) : [];
    this.legacyDropdowns = this.megaRoot ? Array.from(header.querySelectorAll('[data-legacy-dropdown]')) : [];
    this.mobileMenuToggle = header.querySelector('[data-mobile-menu-toggle]');
    this.mobilePanelTriggers = this.megaRoot ? Array.from(this.megaRoot.querySelectorAll('[data-mobile-panel-trigger]')) : [];
    this.mobileAccordionTriggers = this.megaRoot ? Array.from(this.megaRoot.querySelectorAll('[data-mobile-accordion-trigger]')) : [];
    this.drawer = this.megaRoot?.querySelector('[data-mega-drawer]') ?? null;
    this.overlay = this.megaRoot?.querySelector('[data-mega-overlay]') ?? null;
    this.closeDrawerButton = this.megaRoot?.querySelector('[data-mega-close]') ?? null;
    this.titleTarget = this.megaRoot?.querySelector('[data-mega-title]') ?? null;
    this.viewAllTarget = this.megaRoot?.querySelector('[data-mega-view-all]') ?? null;
    this.panels = this.megaRoot ? Array.from(this.megaRoot.querySelectorAll('[data-menu-panel]')) : [];
    this.desktopMedia = window.matchMedia('(min-width: 768px)');
    this.closeTimer = null;
    this.lastTrigger = null;
    this.predictiveSearchTimer = null;
    this.predictiveSearchController = null;
    this.searchHoverRequiresEntry = this.searchPanel?.hasAttribute('data-search-hover-requires-entry') ?? false;
    this.defaultSearchQuery = this.searchPanel?.dataset.defaultSearchQuery?.trim() ?? '';
    this.defaultSearchTemplate = this.searchPanel?.querySelector('[data-default-search-markup]') ?? null;
    this.defaultSearchItems = (this.searchPanel?.dataset.defaultSearchItems ?? '')
      .split('||')
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => {
        const [label, url] = item.split('::');

        return {
          label: (label ?? '').trim(),
          url: (url ?? '').trim()
        };
      })
      .filter((item) => item.label.length && item.url.length);
    this.defaultSearchProductsQuery = this.searchPanel?.dataset.defaultSearchProductsQuery?.trim() ?? '';
    this.defaultSearchMarkup = this.defaultSearchTemplate?.innerHTML?.trim() ?? '';
    this.hasPreloadedDefaultSearch = false;
    this.searchHovered = false;
    this.searchCloseTimer = null;
    this.drawerHoverRequiresEntry = this.megaRoot?.hasAttribute('data-hover-requires-drawer-entry') ?? false;
    this.isHovered = false;

    this.handleDesktopChange = this.handleDesktopChange.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
  }

  init() {
    this.updateLocalDates();
    this.applyAnnouncementBackground();
    this.bindAnnouncementClose();
    this.bindSearch();
    this.preloadDefaultSearch();

    if (!this.megaRoot || !this.drawer || !this.overlay || !this.closeDrawerButton) {
      return;
    }

    this.bindMegaMenu();
    this.syncMegaPosition();
  }

  updateLocalDates() {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');

    this.header.querySelectorAll('.local-date').forEach((el) => {
      el.textContent = `${month}/${day}`;
    });
  }

  detectSafari() {
    return /^((?!chrome|crios|edg|opr|fxios|firefox).)*safari/i.test(navigator.userAgent);
  }

  applyAnnouncementBackground() {
    if (!this.announcementSurface) return;

    const actualColor = this.announcementSurface.dataset.announcementBg;
    if (this.detectSafari()) {
      this.sectionRoot.classList.add('safari-color-applied');
    }

    if (actualColor) {
      this.announcementSurface.style.backgroundColor = actualColor;
    }
  }

  bindAnnouncementClose() {
    if (!this.announcementClose || !this.announcementBar) return;

    this.announcementClose.addEventListener('click', () => {
      this.announcementBar.style.display = 'none';
      this.syncMegaPosition();
    });
  }

  bindSearch() {
    if (!this.searchToggle || !this.searchPanel || !this.searchForm || !this.searchInput) return;

    this.searchToggle.addEventListener('click', () => {
      const isOpen = this.searchPanel.classList.contains('is-open') || this.searchPanel.classList.contains('is-closing');
      if (isOpen) {
        this.closeSearch({ returnFocus: true });
        return;
      }

      this.closeMenu(false);
      this.openSearch();
    });

    this.searchClose?.addEventListener('click', () => {
      this.closeSearch({ returnFocus: true });
    });

    this.searchOverlay?.addEventListener('click', () => {
      this.closeSearch({ returnFocus: false });
    });

    this.searchForm.addEventListener('submit', (event) => {
      if (this.getSearchQuery().length === 0) {
        event.preventDefault();
      }
    });

    this.searchInput.addEventListener('focus', () => {
      if (!this.searchPanel.classList.contains('is-open')) {
        this.openSearch();
      }

      const term = this.getSearchQuery();
      const effectiveTerm = term.length > 0 ? term : this.defaultSearchQuery;
      if (effectiveTerm.length > 0) {
        if (term.length === 0 && this.defaultSearchMarkup) {
          this.renderPredictiveResults(this.defaultSearchMarkup);
        } else {
          this.fetchPredictiveResults(effectiveTerm);
        }
      }
    });

    this.searchInput.addEventListener('input', () => {
      window.clearTimeout(this.predictiveSearchTimer);
      this.predictiveSearchTimer = window.setTimeout(() => {
        const term = this.getSearchQuery();
        const effectiveTerm = term.length > 0 ? term : this.defaultSearchQuery;

        if (effectiveTerm.length === 0) {
          this.clearPredictiveResults();
          return;
        }

        if (!this.searchPanel.classList.contains('is-open')) {
          this.openSearch();
        }

        if (term.length === 0 && this.defaultSearchMarkup) {
          this.renderPredictiveResults(this.defaultSearchMarkup);
          return;
        }

        this.fetchPredictiveResults(effectiveTerm);
      }, 220);
    });

    this.searchPanel.addEventListener('mouseenter', () => {
      if (!this.searchHoverRequiresEntry || !this.desktopMedia.matches) return;

      window.clearTimeout(this.searchCloseTimer);
      this.searchHovered = true;
    });

    this.searchPanel.addEventListener('mouseleave', () => {
      if (!this.searchHoverRequiresEntry || !this.desktopMedia.matches || this.searchHovered !== true) return;

      this.closeSearch();
    });
  }

  bindMegaMenu() {
    this.navTriggers.forEach((trigger) => {
      const key = trigger.dataset.menuKey;
      if (!key) return;

      trigger.addEventListener('mouseenter', () => {
        if (this.drawerHoverRequiresEntry) {
          this.isHovered = false;
        }

        this.openMenu(key, trigger);
      });
      trigger.addEventListener('focus', () => this.openMenu(key, trigger));
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.openMenu(key, trigger);
          window.requestAnimationFrame(() => {
            const firstFocusable = this.drawer.querySelector('.header-mega__panel.is-active a');
            if (firstFocusable) firstFocusable.focus();
          });
        }

        if (event.key === 'Escape') {
          this.closeMenu(true);
        }
      });
    });

    this.closeTriggers.forEach((trigger) => {
      trigger.addEventListener('mouseenter', () => {
        if (this.desktopMedia.matches && !this.drawerHoverRequiresEntry) {
          this.closeMenu(false);
        }
      });
      trigger.addEventListener('focus', () => {
        if (this.desktopMedia.matches) {
          this.closeMenu(false);
        }
      });
    });

    this.legacyDropdowns.forEach((dropdown) => {
      const button = dropdown.querySelector('.header-mega__dropdown-toggle');

      dropdown.addEventListener('mouseenter', () => {
        this.closeMenu(false);
        this.openLegacyDropdown(dropdown);
      });
      dropdown.addEventListener('mouseleave', () => this.closeLegacyDropdown(dropdown));
      dropdown.addEventListener('focusin', () => {
        this.closeMenu(false);
        this.openLegacyDropdown(dropdown);
      });
      dropdown.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;

        this.closeLegacyDropdown(dropdown);
        button?.focus();
      });

      button?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (dropdown.classList.contains('is-open')) {
          this.closeLegacyDropdown(dropdown);
          return;
        }

        this.closeMenu(false);
        this.openLegacyDropdown(dropdown);
      });
    });

    this.mobileMenuToggle?.addEventListener('click', () => {
      if (this.megaRoot.classList.contains('is-open')) {
        this.closeMenu(true);
        return;
      }

      const firstKey = this.mobilePanelTriggers[0]?.dataset.menuKey || this.navTriggers[0]?.dataset.menuKey;
      if (firstKey) {
        this.openMenu(firstKey, this.mobileMenuToggle, { allowMobile: true });
      } else {
        this.openMobileDrawer(this.mobileMenuToggle);
      }
    });

    this.mobileAccordionTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const panelId = trigger.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;
        if (!panel) return;

        const willOpen = trigger.getAttribute('aria-expanded') !== 'true';
        trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        panel.hidden = !willOpen;
      });
    });

    this.mobilePanelTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const key = trigger.dataset.menuKey;
        if (!key) return;
        this.openMenu(key, trigger, { allowMobile: true });
      });
    });

    this.nav?.addEventListener('mouseenter', () => window.clearTimeout(this.closeTimer));
    this.nav?.addEventListener('mouseleave', () => this.scheduleClose());

    this.drawer.addEventListener('mouseenter', () => {
      window.clearTimeout(this.closeTimer);
      if (this.drawerHoverRequiresEntry) {
        this.isHovered = true;
      }
    });
    this.drawer.addEventListener('mouseleave', () => this.scheduleClose());
    this.drawer.addEventListener('focusin', () => window.clearTimeout(this.closeTimer));
    this.drawer.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!this.header.contains(document.activeElement)) {
          this.closeMenu(false);
        }
      }, 0);
    });

    this.overlay.addEventListener('click', () => {
      if (this.desktopMedia.matches && this.drawerHoverRequiresEntry && this.isHovered !== true) {
        return;
      }

      this.closeMenu(false);
    });
    this.closeDrawerButton.addEventListener('click', () => this.closeMenu(true));

    this.header.addEventListener('focusin', (event) => {
      const focusedTrigger = event.target.closest('[data-nav-trigger]');
      if (focusedTrigger?.dataset.menuKey) {
        this.openMenu(focusedTrigger.dataset.menuKey, focusedTrigger);
        return;
      }

      const focusedMobileTrigger = event.target.closest('[data-mobile-panel-trigger]');
      if (focusedMobileTrigger?.dataset.menuKey) {
        this.openMenu(focusedMobileTrigger.dataset.menuKey, focusedMobileTrigger, { allowMobile: true });
        return;
      }

      if (!this.megaRoot.contains(event.target) && !event.target.closest('.header-mega__nav') && !event.target.closest('[data-mobile-menu-toggle]')) {
        this.scheduleClose();
      }
    });

    this.header.addEventListener('keydown', this.handleKeydown);
    this.header.addEventListener('pointerdown', this.handlePointerDown);

    if (typeof this.desktopMedia.addEventListener === 'function') {
      this.desktopMedia.addEventListener('change', this.handleDesktopChange);
    } else if (typeof this.desktopMedia.addListener === 'function') {
      this.desktopMedia.addListener(this.handleDesktopChange);
    }

    window.addEventListener('resize', () => this.syncMegaPosition());
    window.addEventListener('scroll', () => this.syncMegaPosition(), { passive: true });
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.searchPanel?.classList.contains('is-open')) {
      this.closeSearch({ returnFocus: true });
      return;
    }

    if (event.key === 'Escape' && this.megaRoot.classList.contains('is-open')) {
      this.closeMenu(true);
    }
  }

  handlePointerDown(event) {
    if (this.searchPanel?.classList.contains('is-open')) {
      if (!event.target.closest('[data-search-panel]') && !event.target.closest('[data-search-toggle]')) {
        this.closeSearch({ returnFocus: false });
      }
    }

    this.legacyDropdowns.forEach((dropdown) => {
      if (!dropdown.contains(event.target)) {
        this.closeLegacyDropdown(dropdown);
      }
    });

    if (!this.megaRoot.classList.contains('is-open')) return;
    if (event.target.closest('[data-mega-drawer]') || event.target.closest('[data-nav-trigger]')) return;
    if (event.target.closest('[data-mobile-menu-toggle]') || event.target.closest('[data-mobile-panel-trigger]')) return;
    if (event.target.closest('[data-search-panel]') || event.target.closest('[data-search-toggle]')) return;
    if (event.target.closest('[data-cart-container]')) return;
    this.closeMenu(false);
  }

  handleDesktopChange(event) {
    this.closeMenu(false);
    this.closeSearch({ returnFocus: false });
    if (event.matches) this.syncMegaPosition();
  }

  syncMegaPosition() {
    if (!this.megaRoot || !this.header) return;

    let top = 0;

    if (this.desktopMedia.matches) {
      const bounds = this.header.getBoundingClientRect();
      top = Math.max(bounds.bottom, 0);
    } else {
      const announcementContainer =
        this.sectionRoot?.querySelector('.site-announcement__container') ||
        document.querySelector('.site-announcement__container');

      top = announcementContainer ? Math.max(announcementContainer.getBoundingClientRect().height, 0) : 0;
    }

    const height = Math.max(window.innerHeight - top, 0);

    this.megaRoot.style.setProperty('--mega-offset-top', `${top}px`);
    this.megaRoot.style.setProperty('--mega-offset-height', `${height}px`);
  }

  setPanel(key) {
    const activePanel = this.panels.find((panel) => panel.dataset.menuPanel === key);

    this.panels.forEach((panel) => {
      panel.classList.toggle('is-active', panel === activePanel);
    });

    if (!activePanel) return false;

    if (this.titleTarget) {
      this.titleTarget.textContent = activePanel.dataset.menuLabel || '';
    }

    if (this.viewAllTarget) {
      const url = activePanel.dataset.menuUrl;
      if (url) {
        this.viewAllTarget.href = url;
        this.viewAllTarget.hidden = false;
      } else {
        this.viewAllTarget.hidden = true;
      }
    }

    return true;
  }

  openMenu(key, trigger, options = {}) {
    const { allowMobile = false } = options;
    if (!this.desktopMedia.matches && !allowMobile) return;
    if (!this.setPanel(key)) return;

    this.closeSearch({ returnFocus: false });
    window.clearTimeout(this.closeTimer);
    if (this.drawerHoverRequiresEntry) {
      this.isHovered = false;
    }
    this.lastTrigger = trigger || this.lastTrigger;

    this.megaRoot.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.syncMegaPosition();
    this.mobileMenuToggle?.setAttribute('aria-expanded', 'true');

    this.navTriggers.forEach((link) => {
      link.setAttribute('aria-expanded', link.dataset.menuKey === key ? 'true' : 'false');
    });

    this.mobilePanelTriggers.forEach((link) => {
      const isActive = link.dataset.menuKey === key;
      link.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      link.classList.toggle('is-active', isActive);
    });
  }

  openMobileDrawer(trigger) {
    if (this.desktopMedia.matches) return;

    this.closeSearch({ returnFocus: false });
    this.lastTrigger = trigger || this.lastTrigger;
    this.megaRoot.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.mobileMenuToggle?.setAttribute('aria-expanded', 'true');
    this.syncMegaPosition();

    window.requestAnimationFrame(() => {
      this.closeDrawerButton?.focus();
    });
  }

  closeMenu(returnFocus) {
    if (!this.megaRoot) return;

    window.clearTimeout(this.closeTimer);
    this.isHovered = false;
    this.megaRoot.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');

    this.navTriggers.forEach((link) => {
      link.setAttribute('aria-expanded', 'false');
    });

    this.mobileMenuToggle?.setAttribute('aria-expanded', 'false');
    this.mobilePanelTriggers.forEach((link) => {
      link.setAttribute('aria-pressed', 'false');
      link.classList.remove('is-active');
    });

    this.panels.forEach((panel) => panel.classList.remove('is-active'));
    this.mobileAccordionTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      const panelId = trigger.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      if (panel) panel.hidden = true;
    });

    if (this.viewAllTarget) {
      this.viewAllTarget.hidden = true;
    }

    if (this.titleTarget) {
      this.titleTarget.textContent = 'Select a menu';
    }

    if (returnFocus && this.lastTrigger) {
      this.lastTrigger.focus();
    }
  }

  openLegacyDropdown(targetDropdown) {
    if (!targetDropdown) return;

    this.legacyDropdowns.forEach((dropdown) => {
      const isActive = dropdown === targetDropdown;
      const button = dropdown.querySelector('.header-mega__dropdown-toggle');

      dropdown.classList.toggle('is-open', isActive);
      dropdown.dataset.dropdownHovered = 'false';
      button?.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
  }

  closeLegacyDropdown(targetDropdown) {
    if (!targetDropdown) return;

    targetDropdown.classList.remove('is-open');
    targetDropdown.dataset.dropdownHovered = 'false';
    targetDropdown.querySelector('.header-mega__dropdown-toggle')?.setAttribute('aria-expanded', 'false');
  }

  scheduleClose() {
    if (!this.desktopMedia.matches) return;
    if (this.drawerHoverRequiresEntry && this.isHovered !== true) return;

    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => {
      this.closeMenu(false);
    }, 160);
  }

  openSearch() {
    if (!this.searchPanel || !this.searchToggle) return;

    window.clearTimeout(this.searchCloseTimer);
    this.searchPanel.classList.remove('is-closing');
    this.searchPanel.classList.add('is-open');
    this.searchOverlay?.classList.add('is-open');
    this.searchPanel.setAttribute('aria-hidden', 'false');
    this.searchToggle.setAttribute('aria-expanded', 'true');
    document.body.classList.add('header-search-open');
    if (this.searchHoverRequiresEntry && this.desktopMedia.matches) {
      this.searchHovered = false;
    }

    const term = this.getSearchQuery();
    const effectiveTerm = term.length > 0 ? term : this.defaultSearchQuery;
    if (effectiveTerm.length > 0 && this.predictiveSearch) {
      this.predictiveSearch.hidden = false;
      this.predictiveSearch.classList.add('is-active');
      this.predictiveSearch.setAttribute('data-loading', this.defaultSearchMarkup && term.length === 0 ? 'false' : 'true');
      this.searchInput?.setAttribute('aria-expanded', 'true');

      if (term.length === 0 && this.defaultSearchMarkup) {
        this.renderPredictiveResults(this.defaultSearchMarkup);
      }
    }

    window.requestAnimationFrame(() => {
      this.searchInput?.focus();
    });
  }

  closeSearch({ returnFocus = false } = {}) {
    if (!this.searchPanel || !this.searchToggle) return;

    window.clearTimeout(this.searchCloseTimer);
    this.searchHovered = false;
    this.searchOverlay?.classList.remove('is-open');
    document.body.classList.remove('header-search-open');

    if (this.searchHoverRequiresEntry && this.desktopMedia.matches) {
      this.searchPanel.classList.remove('is-open');
      this.searchPanel.classList.add('is-closing');
      this.searchPanel.setAttribute('aria-hidden', 'false');
      this.searchToggle.setAttribute('aria-expanded', 'false');

      this.searchCloseTimer = window.setTimeout(() => {
        if (this.searchPanel.classList.contains('is-open')) return;

        this.searchPanel.classList.remove('is-closing');
        this.searchPanel.setAttribute('aria-hidden', 'true');

        if (returnFocus) {
          this.searchToggle.focus();
        }
      }, 280);

      return;
    }

    this.searchPanel.classList.remove('is-closing');
    this.searchPanel.classList.remove('is-open');
    this.searchPanel.setAttribute('aria-hidden', 'true');
    this.searchToggle.setAttribute('aria-expanded', 'false');

    if (returnFocus) {
      this.searchToggle.focus();
    }
  }

  getSearchQuery() {
    return this.searchInput?.value.trim() ?? '';
  }

  getPredictiveSearchUrl(term) {
    const root = window.Shopify?.routes?.root || '/';
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    const url = new URL(`${normalizedRoot}search/suggest.json`, window.location.origin);

    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,collection,page,article');
    url.searchParams.set('resources[limit]', '8');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    return url.toString();
  }

  preloadDefaultSearch() {
    if (this.hasPreloadedDefaultSearch) return;

    if (this.defaultSearchMarkup.length) {
      this.hasPreloadedDefaultSearch = true;
      return;
    }

    if (this.defaultSearchItems.length) {
      const suggestionLinks = this.defaultSearchItems
        .map((item) => this.renderTextLink(item.url, this.escapeHtml(item.label)))
        .join('');
      const suggestionGroup = suggestionLinks
        ? `
          <div class="predictive-search__group">
            <p class="predictive-search__heading">Suggestions</p>
            <div class="predictive-search__links">${suggestionLinks}</div>
          </div>
        `
        : '';
      const productsTerm = this.defaultSearchProductsQuery || this.defaultSearchQuery;
      const viewAllUrl = this.buildViewAllSearchUrl(productsTerm || this.defaultSearchQuery || '');

      this.defaultSearchMarkup = `
      <div class="predictive-search" role="listbox" aria-label="Search suggestions">
        ${suggestionGroup}
        <a href="${this.escapeAttribute(viewAllUrl)}" class="predictive-search__view-all">View all results</a>
      </div>
    `;
      this.hasPreloadedDefaultSearch = true;

      if (!productsTerm.length) return;

      fetch(this.getPredictiveSearchUrl(productsTerm), {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Predictive search failed with status ${response.status}`);
          }

          return response.json();
        })
        .then((payload) => {
          const products = Array.isArray(payload?.resources?.results?.products) ? payload.resources.results.products : [];
          const productMarkup = products.length
            ? products.map((product) => {
                const imageSrc = product.featured_image?.url || product.image || '';
                const imageMarkup = imageSrc
                  ? `<img src="${this.escapeAttribute(imageSrc)}" alt="${this.escapeAttribute(product.title || '')}" loading="lazy">`
                  : '';

                return `
          <a href="${this.escapeAttribute(product.url || '#')}" class="predictive-search__product">
            <div class="predictive-search__product-media">${imageMarkup}</div>
            <div class="predictive-search__product-copy">
              <span class="predictive-search__product-title">${this.escapeHtml(product.title || '')}</span>
              ${this.renderPredictiveProductPrice(product)}
            </div>
          </a>
        `;
              }).join('')
            : '';
          const productGroup = productMarkup
            ? `
        <div class="predictive-search__group">
          <p class="predictive-search__heading">Products</p>
          <div class="predictive-search__products">${productMarkup}</div>
        </div>
      `
            : '';

          this.defaultSearchMarkup = `
        <div class="predictive-search" role="listbox" aria-label="Search suggestions">
          ${suggestionGroup}
          ${productGroup}
          <a href="${this.escapeAttribute(viewAllUrl)}" class="predictive-search__view-all">View all results</a>
        </div>
      `;
        })
        .catch(() => {});

      return;
    }

    if (!this.defaultSearchQuery.length) return;

    this.hasPreloadedDefaultSearch = true;
    fetch(this.getPredictiveSearchUrl(this.defaultSearchQuery), {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Predictive search failed with status ${response.status}`);
        }

        return response.json();
      })
      .then((payload) => {
        const resources = payload?.resources?.results || {};
        this.defaultSearchMarkup = this.buildPredictiveResultsMarkup(resources, this.defaultSearchQuery);
      })
      .catch(() => {
        this.defaultSearchMarkup = '';
      });
  }

  fetchPredictiveResults(term) {
    if (!this.predictiveSearch || !this.predictiveSearchResults) return;

    if (this.predictiveSearchController) {
      this.predictiveSearchController.abort();
    }

    this.predictiveSearchController = new AbortController();
    this.predictiveSearch.setAttribute('data-loading', 'true');

    fetch(this.getPredictiveSearchUrl(term), {
      signal: this.predictiveSearchController.signal,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Predictive search failed with status ${response.status}`);
        }

        return response.json();
      })
      .then((payload) => {
        const resources = payload?.resources?.results || {};
        this.renderPredictiveResults(this.buildPredictiveResultsMarkup(resources, term));
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.clearPredictiveResults({ preserveInput: true });
      })
      .finally(() => {
        this.predictiveSearch?.setAttribute('data-loading', 'false');
      });
  }

  renderPredictiveResults(markup) {
    if (!this.predictiveSearch || !this.predictiveSearchResults || !this.searchInput) return;

    const hasMarkup = markup.trim().length > 0;
    this.predictiveSearchResults.innerHTML = markup;
    this.predictiveSearch.hidden = !hasMarkup;
    this.predictiveSearch.classList.toggle('is-active', hasMarkup);
    this.searchInput.setAttribute('aria-expanded', hasMarkup ? 'true' : 'false');
  }

  buildPredictiveResultsMarkup(resources, term) {
    const queries = Array.isArray(resources.queries) ? resources.queries : [];
    const products = Array.isArray(resources.products) ? resources.products : [];
    const collections = Array.isArray(resources.collections) ? resources.collections : [];
    const pages = Array.isArray(resources.pages) ? resources.pages : [];
    const articles = Array.isArray(resources.articles) ? resources.articles : [];
    const suggestionItems = [
      ...queries.map((item) => ({ url: item.url, label: this.highlightText(item.text || item.styled_text || '', term) })),
      ...collections.map((item) => ({ url: item.url, label: this.escapeHtml(item.title || '') })),
      ...pages.map((item) => ({ url: item.url, label: this.escapeHtml(item.title || '') })),
      ...articles.map((item) => ({ url: item.url, label: this.escapeHtml(item.title || '') }))
    ].slice(0, 5);

    const groups = [];

    if (suggestionItems.length) {
      const suggestionLinks = suggestionItems
        .map((item) => this.renderTextLink(item.url, item.label))
        .join('');

      if (suggestionLinks.length) {
        groups.push(`
          <div class="predictive-search__group">
            <p class="predictive-search__heading">Suggestions</p>
            <div class="predictive-search__links">${suggestionLinks}</div>
          </div>
        `);
      }
    }

    if (products.length) {
      const productMarkup = products.map((product) => {
        const imageSrc = product.featured_image?.url || product.image || '';
        const imageMarkup = imageSrc
          ? `<img src="${this.escapeAttribute(imageSrc)}" alt="${this.escapeAttribute(product.title || '')}" loading="lazy">`
          : '';

        return `
          <a href="${this.escapeAttribute(product.url || '#')}" class="predictive-search__product">
            <div class="predictive-search__product-media">${imageMarkup}</div>
            <div class="predictive-search__product-copy">
              <span class="predictive-search__product-title">${this.escapeHtml(product.title || '')}</span>
              ${this.renderPredictiveProductPrice(product)}
            </div>
          </a>
        `;
      }).join('');

      groups.push(`
        <div class="predictive-search__group">
          <p class="predictive-search__heading">Products</p>
          <div class="predictive-search__products">${productMarkup}</div>
        </div>
      `);
    }

    if (!groups.length) {
      groups.push(`
        <div class="predictive-search__empty">
          No results for "${this.escapeHtml(term)}"
        </div>
      `);
    }

    const viewAllUrl = this.buildViewAllSearchUrl(term);

    return `
      <div class="predictive-search" role="listbox" aria-label="Search suggestions">
        ${groups.join('')}
        <a href="${this.escapeAttribute(viewAllUrl)}" class="predictive-search__view-all">View all results</a>
      </div>
    `;
  }

  buildViewAllSearchUrl(term) {
    const root = window.Shopify?.routes?.root || '/';
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    const url = new URL(`${normalizedRoot}search`, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('type', 'product,page,article');
    return url.toString();
  }

  renderTextLink(url, label) {
    return `<a href="${this.escapeAttribute(url || '#')}" class="predictive-search__text-link">${label}</a>`;
  }

  highlightText(text, term) {
    const safeText = this.escapeHtml(text);
    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (!escapedTerm) return safeText;

    return safeText.replace(new RegExp(`(${escapedTerm})`, 'ig'), '<mark>$1</mark>');
  }

  formatMoney(cents) {
    const normalizedCents = this.normalizeMoneyToCents(cents);

    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(normalizedCents);
    }

    const amount = normalizedCents / 100;
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    }).format(amount);
  }

  normalizeMoneyToCents(value) {
    if (value == null || value === '') return 0;

    const normalizedString = String(value).replace(/,/g, '').trim();
    const amount = Number(normalizedString);

    if (!Number.isFinite(amount)) return 0;

    if (normalizedString.includes('.') || !Number.isInteger(amount)) {
      return Math.round(amount * 100);
    }

    return Math.round(amount);
  }

  renderPredictiveProductPrice(product) {
    const price = this.normalizeMoneyToCents(product.price_min ?? product.price ?? 0);
    const compareAt = this.normalizeMoneyToCents(
      product.compare_at_price_min ?? product.compare_at_price_max ?? product.compare_at_price ?? 0
    );

    const currentPriceMarkup = `<span class="predictive-search__product-price-current">${this.formatMoney(price)}</span>`;

    if (!compareAt || compareAt <= price) {
      return `<span class="predictive-search__product-price">${currentPriceMarkup}</span>`;
    }

    return `
      <span class="predictive-search__product-price">
        ${currentPriceMarkup}
        <s class="predictive-search__product-price-compare">${this.formatMoney(compareAt)}</s>
      </span>
    `;
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  escapeAttribute(value) {
    return this.escapeHtml(value);
  }

  clearPredictiveResults({ preserveInput = false } = {}) {
    window.clearTimeout(this.predictiveSearchTimer);

    if (this.predictiveSearchController) {
      this.predictiveSearchController.abort();
      this.predictiveSearchController = null;
    }

    if (this.predictiveSearch) {
      this.predictiveSearch.hidden = true;
      this.predictiveSearch.classList.remove('is-active');
      this.predictiveSearch.setAttribute('data-loading', 'false');
    }

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = '';
    }

    if (this.searchInput) {
      this.searchInput.setAttribute('aria-expanded', 'false');
      if (!preserveInput) {
        this.searchInput.value = '';
      }
    }
  }
}

function initHeaderMegaMenus() {
  document.querySelectorAll('[data-header-root]').forEach((header) => {
    if (header.dataset.headerMegaInitialized === 'true') return;
    header.dataset.headerMegaInitialized = 'true';
    new HeaderMegaMenu(header).init();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeaderMegaMenus);
} else {
  initHeaderMegaMenus();
}
